import { useCallback, useState } from 'react';
import _get from 'lodash/get';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { BaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import { validateJql } from '../../common/utils/goal/validators';
import messages from './messages';
import type { State, ValidateJQLRequest } from './types';
import { getUrl } from './utils';

const getJQLErrorMessage = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	jqlValidationError: any,
	// @ts-expect-error - TS2749 - 'MessageDescriptorV2' refers to a value, but is being used as a type here. Did you mean 'typeof MessageDescriptorV2'?
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formatMessage: (MessageDescriptorV2: MessageDescriptorV2, values?: any) => string,
): string | null => {
	if (jqlValidationError) {
		return (
			_get(jqlValidationError, 'errors[0].error') ||
			_get(jqlValidationError, 'message') ||
			formatMessage(messages.genericErrorMessage)
		);
	}
	return null;
};

export const useValidateJQLService = (baseUrl: BaseUrl): State => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<unknown>(null);
	const { formatMessage } = useIntl();
	const onValidateJql = useCallback(
		async (validateJQLRequest: ValidateJQLRequest) => {
			setLoading(true);
			setError(null);
			const localValidationResult = validateJql(
				validateJQLRequest.jqlQuery,
				validateJQLRequest.defaultGoal,
			);
			if (localValidationResult !== undefined) {
				setError(localValidationResult);
				setLoading(false);
				return false;
			}
			try {
				await performPutRequest(getUrl(baseUrl), {
					body: JSON.stringify(validateJQLRequest),
					method: 'PUT',
				});
				fireTrackAnalytics(createAnalyticsEvent({}), 'slaJQL validated');
				setLoading(false);
				return true;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				const jqlErrorMessage = getJQLErrorMessage(err, formatMessage);
				setError(jqlErrorMessage);
				setLoading(false);
				trackFetchErrors({
					error: err,
					id: 'useValidateJQLService',
					packageName: 'jiraServicedeskSlaSettings',
				});
				return false;
			}
		},
		[baseUrl, createAnalyticsEvent, formatMessage],
	);

	const onCancel = useCallback(() => {
		setError(null);
	}, [setError]);

	return [
		{
			loading,
			// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'JqlValidationErrors | undefined'.
			error,
		},
		{
			onValidateJql,
			onCancel,
		},
	];
};
